/**
 * TrustIssuesRoom.js
 * 
 * This is the main room for the game
 */

import toast from "react-hot-toast";
import ExitRoom from "../../../components/Basic/ExitRoom";
import { addPlayerToLobby, checkIfPlayerInLobby, fetchPlayersInTheLobby } from "../../playerHelpers";
import { startImpostorsGame } from "../helpers/startTheGame";
import LobbyOfTrustIssues from "./LobbyOfTrustIssues";
import Stage1ShowingRoles from "./Stage1ShowingRoles";
import DiscussionTimer from "./DiscussionStages";
import ImpostorWord from "./ImpostorWord";
import CodeNames from "./CodeNamesGame";
import ColorCoding from "./ColorCoding";
import FinalStage from "./FinalStage";
import AddPoints from "../components/AddPoints";
import RoomCodeBadge from "../../../Rooms/components/RoomCodeBadge";

const { useState, useEffect } = require("react");
const { TrustIssuesRoomCollection } = require("../../../assets/DatabaseCollections");
const { useClientAuth } = require("../../../hooks/clientAuth");
const { useParams } = require("react-router-dom");
const { fetchRoomDetails } = require("../../../Rooms/helpers/joinRoom");

const TrustIssuesRoom = () => {
    const [room,        setRoom     ]   =   useState(null);
    // const [userTeam,    setUserTeam ]   =   useState(null);
    const [players,     setPlayers  ]   =   useState(null);
    const [dbCollection,            ]   =   useState(TrustIssuesRoomCollection);
    const [isAdmin,     setIsAdmin  ]   =   useState(false);
    const [stage, setStage          ]   =   useState(0);
    // Auth
    const {user                     }   =   useClientAuth();
    // Room Parameter
    const roomID                        =   useParams();

    /**
     * Fetches the room details
     * We are doing it here as well, 
     * because people might share links to the game
     * @param {Object} roomID   -   {room : 'xxxx'}
     * @returns {Object}        -   Information of the room
     */
    useEffect(() => {
        const fetchRoomInfo = async () => {
            const fetchedRoom = await fetchRoomDetails(dbCollection, roomID.room);
            setRoom(fetchedRoom);
            setStage(fetchedRoom?.stage || 0);
        }

        if(user && roomID) fetchRoomInfo();
    }, [dbCollection, roomID, user]);

    /**
     * Checks if the user is already in the lobby,
     * If Yes, return;
     * If No, add them to the lobby
     */
    useEffect(() => {
        const setPlayerInTheLobby = async () => {
            const res = await checkIfPlayerInLobby(dbCollection, room.id, user.userID);
            // If already in the lobby return
            if (res) return;
            // else add them to the lobby
            await addPlayerToLobby(dbCollection, room.id, user);
        }

        if(user && room && stage === 0) setPlayerInTheLobby();

    }, [user, dbCollection, room, stage]);

    /**
     * Checks if the signed in user is the admin
     * (The creator of the room)
     */
    useEffect(() => {
        const isUserAdmin = () => {
            if (user.uid === room?.admin?.userID)
                setIsAdmin(true);
        }
        if (user && room) isUserAdmin();
    }, [user, room]);

    /**
     * Fetches all the players in the lobby
     */
    useEffect(() => {
        const fetchLobby = async () => {
            const allPlayers = await fetchPlayersInTheLobby(dbCollection, room.id);
            setPlayers(allPlayers);
        }
        if(room) fetchLobby();
    }, [dbCollection, room]);

    /**
     * Starts The Game
     */
    const startTheGame = async() => {
        const res = await startImpostorsGame(room.id, players);
        if(!!res) {
            setStage(1);
            setPlayers(res);
            return;
        }
        toast.error('Error starting Game');
    }

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black text-zinc-100">
                <p className="text-lg font-medium">Please login first to continue.</p>
            </div>
        );
    }

    return (
        <div className={`min-h-screen flex flex-col items-center justify-center`}>
            {room?.id ? (
                <>
                    <RoomCodeBadge roomID={room.id} />
                    <div className="absolute top-4 right-6 text-white text-lg font-semibold mt-20">
                        Total Points : {room.totalPoints || 0}
                    </div>
                    {stage === 0 ? (
                        <LobbyOfTrustIssues user={user} room={room} players={players} isAdmin={isAdmin} startTheGame={startTheGame}/>
                    ) : stage === 1 ? (
                        <Stage1ShowingRoles players={players} user={user} roomID={room.id} setStage={setStage}/>
                    ) : stage === 2 ? (
                        <ImpostorWord room={room} stage={stage} user={user} setStage={setStage} isAdmin={isAdmin} />
                    ) : stage === 3 ? (
                        <DiscussionTimer stage={stage} user={user} roomID={room.id} isAdmin={isAdmin} setStage={setStage} />
                    ) : stage === 4 ? (
                        <CodeNames players={players} user={user} roomID={room.id} setStage={setStage} stage={stage}/>
                    ) : stage === 5 ? (
                        <DiscussionTimer stage={stage} user={user} roomID={room.id} isAdmin={isAdmin} setStage={setStage} />
                    ) : stage === 6 ? (
                        <ColorCoding room={room} user={user} players={players} isAdmin={isAdmin} setStage={setStage} stage={stage}/>
                    ) : stage === 7 ? (
                        <DiscussionTimer stage={stage} user={user} roomID={room.id} isAdmin={isAdmin} setStage={setStage} />
                    ) : (
                        <FinalStage players={players} room={room} isAdmin={isAdmin}/>
                    )}
                    {isAdmin && <AddPoints roomID={room.id} /> }
                    <ExitRoom />
                </>
            ) : (
                <div>
                    Room Unavailable
                </div>
            )}
        </div>
    );
}

export default TrustIssuesRoom;